import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import logger from '../../utils/logging/logger';
import { normalizeName } from '../../utils/formatting/nameNormalizer';

export default function useSignUp() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailCheckTimeout, setEmailCheckTimeout] = useState(null);

  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-fill email if passed from login flow
  useEffect(() => {
    if (location.state?.email) {
      setFormData(prev => ({
        ...prev,
        email: location.state.email,
        confirmEmail: location.state.email
      }));
    }
  }, [location.state?.email]);

  // Preserve redirect parameter in localStorage when component mounts or URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const redirectFromQuery = urlParams.get('redirect');
    const redirectFromState = location.state?.from?.pathname;

    // Store redirect in localStorage so it persists through errors
    const redirectToStore = redirectFromQuery || redirectFromState;
    if (redirectToStore) {
      localStorage.setItem('faithinsight_login_redirect', redirectToStore);
    }
  }, [location.search, location.state]);

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 10,
      digit: /\d/.test(password),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };
    return requirements;
  };

  const passwordRequirements = validatePassword(formData.password);

  const checkEmailExists = async (email) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailExists(false);
      return;
    }

    setCheckingEmail(true);
    try {
      // Check if email exists by trying to get user info
      const response = await apiService.checkUserExists(email);
      setEmailExists(response.exists);
    } catch (error) {
      logger.error('Error checking email', { error, email: formData.email });
      setEmailExists(false);
    } finally {
      setCheckingEmail(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');

    // Limit to 10 digits
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Format phone number as user types
    let formattedValue = value;
    if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    if (error) setError('');
    if (success) setSuccess('');

    // Check email existence when email field changes
    if (name === 'email') {
      // Clear existing timeout
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }

      // Set new timeout for debounced email check
      const timeoutId = setTimeout(() => {
        checkEmailExists(value);
      }, 500);

      setEmailCheckTimeout(timeoutId);
    }
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (formData.firstName.trim().length < 2) {
      setError('First name must be at least 2 characters long');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (formData.lastName.trim().length < 2) {
      setError('Last name must be at least 2 characters long');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (emailExists) {
      setError('An account with this email already exists. Please login instead.');
      return false;
    }
    if (!formData.confirmEmail.trim()) {
      setError('Please confirm your email address');
      return false;
    }
    if (formData.email !== formData.confirmEmail) {
      setError('Email addresses do not match');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    // Validate phone number format (exactly 10 digits)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setError('Phone number must contain exactly 10 digits');
      return false;
    }
    if (formData.password.length < 10) {
      setError('Password must be at least 10 characters long');
      return false;
    }
    if (!/\d/.test(formData.password)) {
      setError('Password must contain at least one digit');
      return false;
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password)) {
      setError('Password must contain at least one special character');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    // Normalize names (convert all caps to proper case, format as "First Middle Last")
    const normalizedFirstName = normalizeName(formData.firstName);
    const normalizedLastName = normalizeName(formData.lastName);

    // Strip formatting from phone number before sending to backend
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const result = await signup(formData.email, formData.password, normalizedFirstName, normalizedLastName, phoneDigits);

    if (result.success) {
      setSuccess('Account created successfully! Redirecting...');
      // Navigate immediately without delay
      // Check for redirect in query parameters first
      const urlParams = new URLSearchParams(location.search);
      const redirectFromQuery = urlParams.get('redirect');

      // Then check for redirect in location state
      const redirectFromState = location.state?.from?.pathname;

      // Then check localStorage (preserved from previous visits)
      const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');

      // Prioritize query parameter, then state, then localStorage, then default to dashboard
      const redirectUrl = redirectFromQuery || redirectFromState || redirectFromStorage || '/dashboard';

      // If redirecting to catalog, use window.location for immediate navigation
      if (redirectUrl === '/catalog') {
        // Use window.location to ensure immediate navigation without showing dashboard
        window.location.href = '/portal/catalog?redirect=/catalog';
        // Clear from localStorage after navigation
        if (redirectFromStorage) {
          localStorage.removeItem('faithinsight_login_redirect');
        }
      } else {
        // Clear from localStorage after successful signup for non-catalog redirects
        if (redirectFromStorage) {
          localStorage.removeItem('faithinsight_login_redirect');
        }
        navigate(redirectUrl, { replace: true });
      }
    } else {
      setError(result.error || 'Signup failed. Please try again.');
      // Don't clear redirect on error - keep it for retry
    }

    setLoading(false);
  };

  return {
    formData,
    showPassword, setShowPassword,
    showConfirmPassword, setShowConfirmPassword,
    loading,
    error,
    success,
    emailExists,
    checkingEmail,
    location,
    passwordRequirements,
    handleChange,
    handleSubmit,
  };
}
